import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Affix, Badge, Button, Empty, Select, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router";
import moment from "moment";
import { Report } from "notiflix";

import Navbar from "../../../atoms/navbar";
import Footer from "../../../atoms/footer";
import ImportantInfo from "../../../atoms/important-updates";

import "./styles.css";

import { loginRequest } from "../../../config/api.config";
import { requestToken } from "../../../auth/AuthServices";
import HeaderFarms from "../../../utils/header_farms";
import API from "../../../services";

const STATUS_FILTERS = [
  { label: "Waiting", value: "Waiting Approval", color: "#FF9900" },
  { label: "Approved", value: "Approved", color: "blue" },
  { label: "Rejected", value: "Reject", color: "red" },
  { label: "Canceled", value: "Cancel", color: "green" },
  { label: "Closed", value: "Closed", color: "green" },
];

const DEFAULT_STATUS = "Waiting Approval";

function PATransactionHistory() {
  const { Option } = Select;
  const navigate = useNavigate();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);
  const [session] = useState(
    JSON.parse(localStorage.getItem("session_farms") || "{}")
  );

  const handleLogin = (instanceMsal) => {
    instanceMsal
      .loginRedirect(loginRequest)
      .then(() => { })
      .catch((e) => console.error(e));
  };

  const getRecordStatus = (record) =>
    record?.approval?.status || record?.status || "Waiting Approval";

  const filteredRecords = useMemo(() => {
    if (!statusFilter) return records;
    return records.filter(
      (record) => getRecordStatus(record) === statusFilter
    );
  }, [records, statusFilter]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const accessToken = await requestToken();
      const params = { year: new Date().getFullYear() };
      const response = await API.ListPATransactionsMe(
        HeaderFarms(accessToken),
        params
      );
      setRecords(response.data || []);
    } catch (error) {
      console.error("ListPATransactionsMe error:", error);
      Report.warning(
        "Oops.. something wrong",
        "Sorry, this application is experiencing problems",
        "Okay"
      );
      handleLogin(instance);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const goDetail = (record) => {
    const recordId = record?.record_id || record?.id_record;
    if (!recordId) return;
    navigate(`/pa/view/${recordId}`);
  };

  const statusColor = (status) => {
    const config =
      STATUS_FILTERS.find((item) => item.value === status) || STATUS_FILTERS[0];
    return { color: config.color, label: config.value };
  };

  const renderDescription = (record) => {
    const items = [
      `Period ${record?.period_year || new Date().getFullYear()}`,
    ].filter(Boolean);

    return items.join(" - ");
  };

  return (
    <Fragment>
      <Navbar
        title="History"
        back={true}
        subtitle="Performance Appraisal"
        navigate="/home"
      />
      <div className="container white-back-cr" style={{ minHeight: 500 }}>
        <ImportantInfo />
        <div className="container mt-3 p-3">
          <div className="row">
            <div className="col-md-12 col-sm-12">
              <Affix offsetTop={10}>
                <div className="col-md-6 d-flex justify-content-start">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="middle"
                    onClick={() => navigate("/pa/create")}
                  >
                    Buat Penilaian
                  </Button>
                </div>
              </Affix>
            </div>
          </div>
          <div className="row mb-5">
            <div className="col" />
            <div className="col-md-6 d-flex justify-content-end align-items-center">
              <label style={{ marginRight: 10, marginTop: 5 }}>Filter</label>
              <Select
                value={statusFilter}
                style={{ minWidth: 150 }}
                onChange={setStatusFilter}
              >
                {STATUS_FILTERS.map((status) => (
                  <Option key={status.value} value={status.value}>
                    {status.label}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-10 offset-md-1">
              {filteredRecords.length > 0 ? (
                filteredRecords.map((record, index) => {
                  const status = getRecordStatus(record);
                  const ribbon = statusColor(status);

                  return (
                    <Fragment key={`${record.id_record || index}`}>
                      <Badge.Ribbon color={ribbon.color} text={status}>
                        <div
                          className="list-style p-3 px-4 mb-3"
                          onClick={() => goDetail(record)}
                        >
                          <div className="title mb-1">
                            <p className="mb-0" style={{ fontSize: 12 }}>
                              {session?.fullname || record?.fullname || ""}
                            </p>
                          </div>
                          <div>
                            <p
                              className="mb-0"
                              style={{
                                fontSize: 11,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {renderDescription(record)}
                            </p>
                          </div>
                          <div className="hour d-flex justify-content-between">
                            <p
                              style={{ color: "#B7B7B7", fontSize: 11 }}
                            >
                              {moment(record.updated_at).format("LLL")}
                            </p>
                          </div>
                        </div>
                      </Badge.Ribbon>
                    </Fragment>
                  );
                })
              ) : (
                !loading && <Empty className="mt-5" />
              )}

              {filteredRecords.length > 0 && (
                <div
                  className="fs-6 text-center mt-5"
                  style={{ color: "#B7B7B7" }}
                >
                  No more data
                </div>
              )}

              {loading && (
                <div
                  className="fs-6 text-center mt-5"
                  style={{ color: "#B7B7B7" }}
                >
                  <Spin size="large" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </Fragment>
  );
}

export default PATransactionHistory;
